<script setup lang="ts">
import MeService from '@/core/shared/me/me.service';
import SubscriptionService from '@/core/shared/subscription/subscription.service';
import { DrawerService } from '@/core/shared/drawer/drawer.service';
import { computed, ref } from 'vue';
import { useDisplay } from 'vuetify';
import UserInfo from '../UserInfo/UserInfo.vue';
import CompanyInfo from '../CompanyInfo/CompanyInfo.vue';
import GetHelpContent from '../GetHelp/GetHelpContent.vue';
import InviteTeammateContent from '../InviteTeammate/InviteTeammateContent.vue';
import CompaniesContent from '../CompanyInfo/CompaniesContent.vue';
import MobileDrawerItem from './MobileDrawerItem.vue';
import PlanUsage from '../PlanUsage/PlanUsage.vue';
import Divider from '../../Divider/Divider.vue';
import ChevronLeftSvg from '@/assets/svg/chevron-left.svg';
import { RouterLink } from 'vue-router';
import AuthenticationService from '@/core/shared/authentication/authentication.service';
import { useMainNavItems } from '@/composables/useMainNavItems';
import { usePrivacyStore } from '@/core/shared/privacy/privacy.store';
import { useHelpButton } from '@/composables/useHelpButton';

const display = useDisplay();
const meService = new MeService();
const drawerService = new DrawerService();
const subscriptionService = new SubscriptionService();
const authenticationService = new AuthenticationService();

const isMobile = computed(() => display.smAndDown.value);
const detailView = ref<'get-help' | 'invite-teammate' | 'companies'>();

const canInviteTeammates = computed(() => meService.isEmployerAdmin);

// Phones have no left nav ? button, so Get help also links to this screen's help page.
const { helpPageUrl, onHelpLinkClick } = useHelpButton();

// Primary navigation — shared source of truth with DesktopDrawer.
const { items: mainNavItems } = useMainNavItems();

// usePrivacyMode() in App.vue owns the body-class side effect, so this only flips
// the store. Closing the drawer makes the masking visible on the page behind it.
const privacyStore = usePrivacyStore();
const privacyLabel = computed(() =>
  privacyStore.isActive ? 'Show sensitive data' : 'Hide sensitive data',
);
function togglePrivacy() {
  privacyStore.toggle();
  drawerService.isMobileDrawerOpen = false;
}

function onUserClick() {
  const userProfileId = meService.userProfile?.id;
  meService.redirectToUI2(`/employer/users/${userProfileId}`);
}

function onLogoutClick() {
  authenticationService.logout();
  meService.redirectToUI2('/employer/auth/logout');
}
</script>

<template>
  <v-navigation-drawer
    v-if="isMobile"
    :model-value="drawerService.isMobileDrawerOpen"
    temporary
    floating
    width="376"
    location="right"
    class="rounded-l-xl"
  >
    <!-- General View -->
    <div v-if="!detailView" class="flex h-full flex-col justify-between py-4">
      <div class="flex flex-col font-bold">
        <!-- Primary navigation (shared with DesktopDrawer via useMainNavItems) -->
        <template v-for="(item, i) in mainNavItems" :key="item.key">
          <template v-if="item.visible">
            <Divider v-if="i > 0" class="mx-3" />
            <RouterLink
              v-if="item.to"
              :to="item.to"
              class="drawer-item"
              active-class="drawer-item-active"
            >
              <MobileDrawerItem>{{ item.label }}</MobileDrawerItem>
            </RouterLink>
            <div v-else-if="item.onClick" class="drawer-item" @click="item.onClick()">
              <MobileDrawerItem>{{ item.label }}</MobileDrawerItem>
            </div>
          </template>
        </template>

        <Divider class="mx-3" />

        <MobileDrawerItem v-if="canInviteTeammates" link @click="detailView = 'invite-teammate'"
          >Invite teammate</MobileDrawerItem
        >

        <Divider class="mx-3" />

        <RouterLink
          to="/settings"
          class="drawer-item"
          active-class="drawer-item-active"
          @click="drawerService.isMobileDrawerOpen = false"
        >
          <MobileDrawerItem>Settings</MobileDrawerItem>
        </RouterLink>

        <Divider class="mx-3" />

        <MobileDrawerItem link @click="detailView = 'get-help'">Get help</MobileDrawerItem>

        <Divider class="mx-3" />

        <a href="https://refer.factoryfix.com" target="_blank" rel="noopener noreferrer">
          <MobileDrawerItem>Referral</MobileDrawerItem>
        </a>

        <Divider class="mx-3" />

        <MobileDrawerItem @click="togglePrivacy">{{ privacyLabel }}</MobileDrawerItem>
      </div>

      <PlanUsage
        class="mx-2"
        :total-job-slots="subscriptionService.jobSlots"
        :used-job-slots="subscriptionService.usedJobSlots"
      />

      <div class="flex flex-col">
        <Divider class="mx-3" />

        <MobileDrawerItem class="h-12">
          <UserInfo
            v-if="meService.userProfile"
            :user="meService.userProfile"
            @click="onUserClick"
          />
        </MobileDrawerItem>

        <Divider class="mx-3" />

        <MobileDrawerItem link class="h-12" @click="detailView = 'companies'">
          <CompanyInfo v-if="meService.employer" :employer="meService.employer" />
        </MobileDrawerItem>

        <Divider class="mx-3" />

        <MobileDrawerItem class="mt-6 font-bold" @click="onLogoutClick">Logout</MobileDrawerItem>
      </div>
    </div>

    <!-- Detail View -->
    <div v-else class="py-4">
      <div class="flex cursor-pointer items-center px-2" @click="detailView = undefined">
        <img :src="ChevronLeftSvg" class="h-5 w-5" />
        <span class="ml-1 text-sm font-semibold">Back</span>
      </div>

      <template v-if="detailView === 'companies'">
        <div class="menu-card border-0 py-2 shadow-none">
          <CompaniesContent />
        </div>
      </template>

      <template v-else-if="detailView === 'get-help'">
        <a
          :href="helpPageUrl()"
          target="_blank"
          rel="noopener"
          class="mt-2 block"
          data-test="mobile-help-center-link"
          @click="onHelpLinkClick"
        >
          <MobileDrawerItem>
            <span class="font-semibold text-highlight-600">Open help center ↗</span>
          </MobileDrawerItem>
        </a>
        <Divider class="mx-3" />
        <GetHelpContent />
      </template>

      <template v-else-if="detailView === 'invite-teammate'">
        <InviteTeammateContent v-if="canInviteTeammates" />
      </template>
    </div>
  </v-navigation-drawer>
</template>
